/**
 * Transporte HTTP (Fase M4): cuando la app corre en el NAVEGADOR servida por el servidor (PWA),
 * habla con el backend por `POST /api/invoke` (mismo contrato que el puente de Electron). La sesión
 * viaja en la cookie httpOnly, así que usamos `credentials: "same-origin"`. En Electron se sigue
 * usando el puente del preload; en desarrollo (vite dev), el transporte simulado.
 */
import type { Transport, BridgeResponse } from "./transport";

export class HttpTransport implements Transport {
  async invoke<T>(channel: string, payload?: unknown): Promise<BridgeResponse<T>> {
    try {
      const res = await fetch("/api/invoke", {
        method: "POST", credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ channel, payload }),
      });
      if (res.status === 401) return { ok: false, error: { code: "unauthorized", message: "Sesión requerida" } };
      return await res.json() as BridgeResponse<T>;
    } catch (e) {
      return { ok: false, error: { code: "network", message: (e as Error).message } };
    }
  }
  subscribe(): () => void { return () => {}; }
}

/** ¿Corremos como app web (PWA servida por el servidor), no en Electron ni en dev? */
export function isWebClient(): boolean {
  return typeof window !== "undefined" && !window.fabianMeli && import.meta.env.PROD;
}

// ── Endpoints de autenticación ───────────────────────────────────────────────
export interface AuthStatus { readonly configured: boolean; readonly authenticated: boolean }

export async function fetchAuthStatus(): Promise<AuthStatus> {
  try {
    const res = await fetch("/api/auth/status", { credentials: "same-origin" });
    const j = await res.json() as { configured?: boolean; authenticated?: boolean };
    return { configured: !!j.configured, authenticated: !!j.authenticated };
  } catch { return { configured: false, authenticated: false }; }
}

export async function authSetup(password: string): Promise<{ ok: boolean; detail: string; otpauthUri?: string; totpSecret?: string }> {
  const res = await fetch("/api/auth/setup", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ password }) });
  return await res.json() as { ok: boolean; detail: string; otpauthUri?: string; totpSecret?: string };
}

export async function authLogin(password: string, code: string): Promise<{ ok: boolean; detail: string }> {
  const res = await fetch("/api/auth/login", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ password, code }) });
  return await res.json() as { ok: boolean; detail: string };
}

export async function authLogout(): Promise<void> {
  try { await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" }); } catch { /* noop */ }
}

// ── Alertas por correo (M7) ──────────────────────────────────────────────────
export interface AlertsConfig { readonly enabled: boolean; readonly user: string; readonly to: string; readonly configured: boolean }

export async function fetchAlertsConfig(): Promise<AlertsConfig | null> {
  try { const r = await fetch("/api/alerts/config", { credentials: "same-origin" }); const j = await r.json() as { ok: boolean; config?: AlertsConfig }; return j.ok && j.config ? j.config : null; } catch { return null; }
}
export async function saveAlertsConfig(input: { enabled: boolean; user: string; pass?: string; to: string }): Promise<AlertsConfig | null> {
  try { const r = await fetch("/api/alerts/config", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(input) }); const j = await r.json() as { ok: boolean; config?: AlertsConfig }; return j.ok && j.config ? j.config : null; } catch { return null; }
}
export async function sendAlertsTest(): Promise<{ ok: boolean; detail: string }> {
  try { const r = await fetch("/api/alerts/test", { method: "POST", credentials: "same-origin" }); return await r.json() as { ok: boolean; detail: string }; } catch (e) { return { ok: false, detail: (e as Error).message }; }
}

// ── Estado del servidor (M8.2) ───────────────────────────────────────────────
export interface ServerStatus {
  readonly ok: boolean;
  readonly version: string;
  readonly uptimeSec: number;
  readonly startedAt: number;
  readonly backups: { last: { ok: boolean; at: number; detail: string; file?: string; bytes?: number } | null; count: number; newest?: number; dir: string };
  readonly alerts: { enabled: boolean; configured: boolean };
  readonly watch: { lastRunAt: number | null; lastNewFindings: number | null };
}
export async function fetchServerStatus(): Promise<ServerStatus | null> {
  try { const r = await fetch("/api/status", { credentials: "same-origin" }); if (!r.ok) return null; const j = await r.json() as ServerStatus & { ok: boolean }; return j.ok ? j : null; } catch { return null; }
}
